import { useMutation, useQuery } from "@tanstack/react-query";
import { useAdminApplication, useAdminSession } from "../../app/providers";
import { transactionKeys } from "../transactions/queries";
import { executeTransfer, getTransferDetail, getTransfers } from "./api";
import type { TransferListQuery } from "./api";

export const transferKeys = {
  all: (sessionEpoch: number) => ["admin", sessionEpoch, "transfers"] as const,
  lists: (sessionEpoch: number) => [...transferKeys.all(sessionEpoch), "list"] as const,
  list: (sessionEpoch: number, query: TransferListQuery) => (
    [...transferKeys.lists(sessionEpoch), query] as const
  ),
  detail: (sessionEpoch: number, transferId: number) => (
    [...transferKeys.all(sessionEpoch), "detail", transferId] as const
  ),
};

export function useTransferList(query: TransferListQuery) {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  return useQuery({
    queryKey: transferKeys.list(session.sessionEpoch, query),
    queryFn: ({ signal }) => getTransfers(apiClient, query, signal),
    refetchOnMount: "always",
    staleTime: 0,
  });
}

export function useTransferDetail(transferId: number) {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  return useQuery({
    queryKey: transferKeys.detail(session.sessionEpoch, transferId),
    queryFn: ({ signal }) => getTransferDetail(apiClient, transferId, signal),
    refetchOnMount: "always",
    staleTime: 0,
  });
}

export function useExecuteTransfer(transferId: number, transactionId?: number | null) {
  const { apiClient, queryClient, sessionLifecycle } = useAdminApplication();
  const session = useAdminSession();
  return useMutation({
    mutationFn: async () => {
      const request = sessionLifecycle.createRequestController();
      try {
        return await executeTransfer(apiClient, transferId, request.controller.signal);
      } finally {
        request.release();
      }
    },
    onSuccess: async () => {
      const invalidations: Array<Promise<unknown>> = [
        queryClient.invalidateQueries({ queryKey: transferKeys.lists(session.sessionEpoch) }),
        queryClient.invalidateQueries({
          queryKey: transferKeys.detail(session.sessionEpoch, transferId),
          refetchType: "none",
        }),
        queryClient.invalidateQueries({ queryKey: transactionKeys.lists(session.sessionEpoch) }),
      ];
      if (transactionId) {
        invalidations.push(queryClient.invalidateQueries({
          queryKey: transactionKeys.detail(session.sessionEpoch, transactionId),
          refetchType: "none",
        }));
      }
      await Promise.all(invalidations);
    },
  });
}
